import { Component } from '@theme/component';

class TestimonialsCarousel extends Component {
  /**
   * Virtual position on the cloned track. Real slides occupy [0, slideCount);
   * -1 and slideCount are clone positions passed through while looping.
   * @type {number}
   */
  #position = 0;

  /** @type {HTMLElement[]} */
  #beforeClones = [];

  /** @type {HTMLElement[]} */
  #afterClones = [];

  /** @type {number | undefined} */
  #snapTimer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#buildClones();
    this.#position = this.#clampIndex(parseInt(this.dataset.initialSlide || '0', 10));
    this.refs.previous?.addEventListener('click', this.#previous);
    this.refs.next?.addEventListener('click', this.#next);
    this.refs.track?.addEventListener('click', this.#handleTrackClick);
    this.addEventListener('keydown', this.#handleKeydown);

    if (this.refs.viewport instanceof HTMLElement) {
      this.#resizeObserver = new ResizeObserver(this.#centerActive);
      this.#resizeObserver.observe(this.refs.viewport);
    }

    requestAnimationFrame(() => this.#goTo(this.#position));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.previous?.removeEventListener('click', this.#previous);
    this.refs.next?.removeEventListener('click', this.#next);
    this.refs.track?.removeEventListener('click', this.#handleTrackClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#resizeObserver?.disconnect();
    window.clearTimeout(this.#snapTimer);
  }

  get #slides() {
    return Array.isArray(this.refs.slide) ? this.refs.slide : [];
  }

  #allItems() {
    return [...this.#beforeClones, ...this.#slides, ...this.#afterClones];
  }

  /**
   * @param {number} index
   */
  #clampIndex(index) {
    const slideCount = this.#slides.length;
    if (!slideCount) return 0;
    return Math.max(0, Math.min(index || 0, slideCount - 1));
  }

  /**
   * @param {number} index
   */
  #wrapIndex(index) {
    const slideCount = this.#slides.length;
    if (!slideCount) return 0;
    return ((index % slideCount) + slideCount) % slideCount;
  }

  /**
   * @param {number} position
   * @returns {HTMLElement | undefined}
   */
  #elementAt(position) {
    const slideCount = this.#slides.length;
    if (!slideCount) return undefined;
    if (position < 0) return this.#beforeClones[position + slideCount];
    if (position >= slideCount) return this.#afterClones[position - slideCount];
    return this.#slides[position];
  }

  #buildClones() {
    const track = this.refs.track;
    const slides = this.#slides;
    if (!(track instanceof HTMLElement) || slides.length < 2 || this.#beforeClones.length) return;

    /**
     * @param {HTMLElement} slide
     */
    const makeClone = (slide) => {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.removeAttribute('ref');
      clone.removeAttribute('aria-current');
      clone.removeAttribute('aria-label');
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');
      for (const name of clone.getAttributeNames()) {
        if (name.startsWith('data-shopify')) clone.removeAttribute(name);
      }
      return clone;
    };

    this.#beforeClones = slides.map(makeClone);
    this.#afterClones = slides.map(makeClone);

    track.prepend(...this.#beforeClones);
    track.append(...this.#afterClones);
  }

  /**
   * @param {number} position
   */
  #goTo(position) {
    if (!this.#slides.length) return;

    window.clearTimeout(this.#snapTimer);
    this.#position = position;
    this.#applyActive();

    requestAnimationFrame(this.#centerActive);
    this.#snapTimer = window.setTimeout(() => {
      this.#centerActive();
      this.#normalize();
    }, 430);
  }

  /**
   * @param {number} delta
   */
  #step(delta) {
    this.#normalize();
    this.#goTo(this.#position + delta);
  }

  #applyActive() {
    const active = this.#elementAt(this.#position);
    const current = this.#wrapIndex(this.#position);

    for (const item of this.#allItems()) {
      item.classList.toggle('is-active', item === active);
    }

    this.#slides.forEach((slide, index) => {
      slide.toggleAttribute('aria-current', index === current);
    });
  }

  /**
   * If the active position landed on a clone, jump to the equivalent real
   * slide with transitions suppressed so the loop is seamless.
   */
  #normalize = () => {
    const slideCount = this.#slides.length;
    if (!slideCount) return;
    if (this.#position >= 0 && this.#position < slideCount) return;

    this.#position = this.#wrapIndex(this.#position);
    this.classList.add('is-snapping');
    this.#applyActive();
    this.#centerActive();

    const track = this.refs.track;
    if (track instanceof HTMLElement) void track.offsetWidth;
    this.classList.remove('is-snapping');
  };

  #centerActive = () => {
    const viewport = this.refs.viewport;
    const track = this.refs.track;
    const activeSlide = this.#elementAt(this.#position);

    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !(activeSlide instanceof HTMLElement)) {
      return;
    }

    const activeCenter = activeSlide.offsetLeft + activeSlide.offsetWidth / 2;
    const offset = viewport.clientWidth / 2 - activeCenter;

    track.style.transform = `translate3d(${offset}px, 0, 0)`;
  };

  #previous = (event) => {
    event.preventDefault();
    this.#step(-1);
  };

  #next = (event) => {
    event.preventDefault();
    this.#step(1);
  };

  #handleTrackClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('.testimonials__item');
    if (!(slide instanceof HTMLElement)) return;

    this.#normalize();

    const slideCount = this.#slides.length;
    const realIndex = this.#slides.indexOf(slide);
    const beforeIndex = this.#beforeClones.indexOf(slide);
    const afterIndex = this.#afterClones.indexOf(slide);

    let position = null;
    if (realIndex >= 0) position = realIndex;
    else if (beforeIndex >= 0) position = beforeIndex - slideCount;
    else if (afterIndex >= 0) position = afterIndex + slideCount;

    if (position === null || this.#wrapIndex(position) === this.#wrapIndex(this.#position)) return;
    this.#goTo(position);
  };

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
